import { Alert, Card, CardContent, Chip, Grid, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { readPlans } from '../api/endpoints';
import type { SubscriptionPlanView } from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageHeader } from '../components/PageHeader';

const formatPrice = (cents: number, currency: string): string =>
  new Intl.NumberFormat('sk-SK', { style: 'currency', currency }).format(cents / 100);

export const PlansPage = () => {
  const { data, error, isLoading, refetch } = useQuery<SubscriptionPlanView[]>({
    queryKey: ['plans'],
    queryFn: readPlans,
  });

  const items = data ?? [];

  return (
    <>
      <PageHeader
        title="Predplatné"
        subtitle="Plány a ich rozsah. Model je pripravený, no zatiaľ sa nevymáha."
      />

      <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
        V tejto verzii sa žiadne oprávnenie nevymáha a neúčtuje sa nič — každý účet sa správa, akoby mal plný
        prístup. Až sa zapne fakturácia, zmení sa jediné miesto v API.
      </Alert>

      <ErrorNotice error={error} onRetry={() => void refetch()} />

      {isLoading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Skeleton variant="rounded" height={228} />
            </Grid>
          ))}
        </Grid>
      ) : items.length === 0 ? (
        <Paper variant="outlined">
          <EmptyState
            title="Zatiaľ nie je definovaný žiadny plán"
            detail="Plán vytvoríte cez POST /subscription-plans. Formulár na jeho zadanie priamo tu zatiaľ nie je."
          />
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {items.map((view: SubscriptionPlanView) => (
            <Grid item xs={12} md={4} key={view.id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">{view.name}</Typography>
                    <Chip
                      size="small"
                      label={view.status}
                      color={view.status === 'active' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Stack>

                  <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 1.5 }}>
                    <Typography variant="h4" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatPrice(view.priceCents, view.currency)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      / {view.billingPeriod}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.55 }}>
                    {view.description ?? '—'}
                  </Typography>

                  <Stack spacing={0.75}>
                    {view.entitlements.map((entitlement) => (
                      <Stack key={entitlement.key} direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {entitlement.key}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 560 }}>
                          {entitlement.isUnlimited ? 'bez obmedzenia' : (entitlement.limitValue ?? '—')}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};
